import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import { siteWide } from '@helpers/grid';
import { Device, from, until } from '@helpers/media';
import HeadingSummaryBlockStyles from '@stories/Components/Misc/HeadingSummaryBlock/HeadingSummaryBlock.styles';
import SectionWrapper, { BackgroundVariant, } from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import SectionWrapperStyles from '@stories/Components/Misc/SectionWrapper/SectionWrapper.styles';
import styled, { css } from 'styled-components';
import { shouldAnimate, transition } from './../../../helpers/animate';
const Container = styled(SectionWrapper) `
  --iconColour: ${brand.global.white};
  --secondary: ${brand.primary.purple.dark};
  --tertiary: ${brand.primary.purple.light};
  --sectionSpacing: ${fluid(80, 146)};

  @media print {
    display: none;
  }

  overflow: hidden;
  background-color: transparent;

  &[data-primary=${BackgroundVariant.Blue}] {
    --primary: ${brand.primary.blue.light};
    --sectionColour: ${brand.global.white};
    --sectionBackground: ${brand.primary.blue.dark};

    @media ${from(Device.TabletLarge)} {
      --primary: ${brand.primary.blue.dark};
      --sectionBackground: ${brand.primary.blue.light};
    }
  }

  &[data-primary=${BackgroundVariant.White}] {
    --primary: ${brand.primary.blue.dark};
    --sectionColour: ${brand.primary.blue.dark};
    --sectionBackground: ${brand.global.white};

    @media ${from(Device.TabletLarge)} {
      --primary: ${brand.global.white};
      --sectionBackground: ${brand.primary.blue.dark};
    }
  }

  ${SectionWrapperStyles.Inner} {
    padding-top: 80px;
    padding-bottom: 80px;

    @media ${until(Device.TabletLarge)} {
      padding: 0;
    }
  }
`;
const Wrapper = styled.div `
  ${siteWide};
  padding-top: var(--sectionSpacingTop);
  padding-bottom: var(--sectionSpacingBottom);

  position: relative;
  overflow: hidden;
  background-color: var(--sectionBackground);
  border-radius: ${fluid(10, 20)};

  @media ${until(Device.TabletLarge)} {
    margin: 0;
    width: 100%;
    border-radius: 0;
  }

  &::before,
  &::after,
  ${HeadingSummaryBlockStyles.Container}::after {
    ${transition('transform', 0.5)};

    content: '';
    position: absolute;
  }

  &::before {
    top: 0;
    right: 0;
    background-color: var(--primary);
    width: 800px;
    height: 800px;
    transform: translate(80%, -75%) rotate(-45deg);
    z-index: 1;
  }

  &::after,
  ${HeadingSummaryBlockStyles.Container}::after {
    background-color: var(--tertiary);
    right: 0;
    bottom: 0;
    transition-delay: 150ms;
    transform: translate(75%, 65%) rotate(-52deg);
  }

  &::after {
    width: 200px;
    height: 200px;
    background-color: var(--tertiary);
    z-index: 1;
  }

  ${HeadingSummaryBlockStyles.Container}::after {
    background-color: var(--secondary);
    width: 375px;
    height: 375px;
  }

  @media ${until(Device.TabletLarge)} {
    // Added to stop content from clashing with the background
    ${HeadingSummaryBlockStyles.Content} {
      padding-right: 50px;
    }
  }

  @media ${until(Device.TabletSmall)} {
    

    // Added to force buttons to wrap and stop them from clashing with the background
    ${HeadingSummaryBlockStyles.ButtonWrapper} {    
      padding-right: 100px;
    }
  }

  @media ${from(Device.TabletLarge)} {
    &::before {
      ${transition('transform, background-image', 0.6)};

      bottom: 50%;
      left: auto;
      right: 0;
      border-radius: 0 0 66px 0;
      width: 1600px;
      height: 1600px;
      transform: translate(-32%, 55%) rotate(-45deg);
      background-image: linear-gradient(0deg, var(--secondary) 6.25%, var(--primary) 6.25%);
      top: auto;
    }

    &::after {
      width: 800px;
      height: 800px;
      bottom: 50%;
      transform: translate(33.5%, 107%) rotate(-45deg) skew(10deg, 0deg);
      border-radius: 0 66px 0 0;
      z-index: 0;
    }

    ${HeadingSummaryBlockStyles.Container}::after {
      display: none;
    }
  }

  @media ${from(Device.DesktopLarge)} {
    &::before {
      transform: translate(-45%, 55%) rotate(-45deg);
    }

    &::after {
      transform: translate(7.5%, 107%) rotate(-45deg) skew(10deg, 0deg);
    }

    ${HeadingSummaryBlockStyles.Container}::after {
      width: 50vw;
    }
  }

  [data-animate='false'] & {
    &::before {
      transform: translate(90%, -100%) rotate(-45deg);

      @media ${from(Device.TabletLarge)} {
        transform: translate(-25%, 15%) rotate(0deg);
        background-image: none;
      }

      @media ${from(Device.DesktopLarge)} {
        transform: translate(-45%, 18%) rotate(0deg);
      }
    }

    @media ${until(Device.TabletLarge)} {
      &:after,
      ${HeadingSummaryBlockStyles.Container}::after {
        transform: translate(95%, 100%) rotate(-52deg);
      }
    }

    @media ${from(Device.TabletLarge)} {
      &::after {
        transform: translate(-25%, 150%) rotate(-45deg) skew(10deg, 0deg);
      }
    }

    ${HeadingSummaryBlockStyles.Container} {
      opacity: 0;
      transform: translateY(20px);
    }
  }

  ${HeadingSummaryBlockStyles.Container} {
    ${transition('opacity, transform')};

    position: relative;
    z-index: 1;
    transition-delay: 350ms;

    @media ${from(Device.TabletLarge)} {
      background-color: var(--primary);
      width: 45%;
    }
  }

  ${HeadingSummaryBlockStyles.Content} {
    position: relative;
    z-index: 1;
  }

  ${HeadingSummaryBlockStyles.Content} p {
    font-size: ${fluid(14, 20)};
    line-height: ${fluid(22, 28)};
  }
`;
const IconsWrapper = styled.div `
  display: none;

  @media ${from(Device.TabletLarge)} {
    ${transition('opacity, transform')};

    display: block;
    position: absolute;
    top: 0;
    right: 0;
    width: 50vw;
    height: 100%;
    transition-delay: 600ms;
  }

  @media ${from(Device.DesktopLarge)} {
    width: 47vw;
  }

  @media ${from(Device.ActualDesktop)} {
    width: 43vw;
  }

  ${Container}[data-animate='false'] & {
    opacity: 0;
    transform: translateY(20px);
  }
`;
const Icon = styled.div `
  position: absolute;
  display: grid;
  place-content: center;
  color: var(--iconColour);

  svg {
    grid-area: 1 / 1 / 2 / 2;
    width: 100%;

    ${shouldAnimate(css `
      animation: var(--fadeType) 12s linear infinite;
    `)}

    &:nth-child(1) {
      --fadeType: iconFade1;
    }

    &:nth-child(2) {
      --fadeType: iconFade2;
    }

    &:nth-child(3) {
      --fadeType: iconFade3;
    }

    ${Container}[data-animate='false'] & {
      animation-play-state: paused;
    }
  }

  &:nth-child(1),
  &:nth-child(2),
  &:nth-child(7),
  &:nth-child(8) {
    width: 2.5vw;
  }

  &:nth-child(3),
  &:nth-child(6),
  &:nth-child(9) {
    width: 3vw;
  }

  &:nth-child(4) {
    width: 2vw;
  }

  &:nth-child(5) {
    width: 3.5vw;
  }

  &:nth-child(1) {
    top: 2.5%;
    left: 15%;
  }

  &:nth-child(2) {
    top: 4%;
    left: 54%;
  }

  &:nth-child(3) {
    top: 2%;
    right: 5%;
  }

  &:nth-child(4) {
    top: 14%;
    left: 33%;
  }

  &:nth-child(5) {
    top: 11%;
    left: 72%;
  }

  &:nth-child(6) {
    top: 29%;
    left: 41%;
  }

  &:nth-child(7) {
    top: 33%;
    left: 67%;
  }

  &:nth-child(8) {
    top: 27%;
    right: 8%;
  }

  &:nth-child(9) {
    top: 54%;
    right: 7%;
  }

  @keyframes iconFade1 {
    0% {
      opacity: 1;
    }

    20% {
      opacity: 1;
    }

    33% {
      opacity: 0;
    }

    86% {
      opacity: 0;
    }

    100% {
      opacity: 1;
    }
  }

  @keyframes iconFade2 {
    0% {
      opacity: 0;
    }

    20% {
      opacity: 0;
    }

    33% {
      opacity: 1;
    }

    50% {
      opacity: 1;
    }

    66% {
      opacity: 0;
    }

    100% {
      opacity: 0;
    }
  }

  @keyframes iconFade3 {
    0% {
      opacity: 0;
    }

    50% {
      opacity: 0;
    }

    66% {
      opacity: 1;
    }

    86% {
      opacity: 1;
    }

    100% {
      opacity: 0;
    }
  }
`;
const JoinCtaCardStyles = {
    Container,
    IconsWrapper,
    Icon,
    Wrapper,
};
export default JoinCtaCardStyles;
